"use client";
import React, {memo, useEffect, useState} from "react";
import SearchHomeInput from "@/components/ui/inputs/search-home-input";
import Image from "next/image";
import ElanzaLogo from "@/public/logo/elanza-logo.svg";
import BagButtonComponent from "@/components/global/buttons/bagButtonComponent";
import UserButtonComponent from "@/components/global/buttons/userButtonComponent";
import MegaMenu from "@/components/ui/menu/desktop-menu/mega-menu";
import ContainerLayout from "@/components/layout/containerLayout";
import Link from "next/link";
import UserButtonProvider from "@/components/global/buttons/userButtonProvider";
import TopBarComponent from "@/components/ui/top-bar/topBarComponent";
import {useTopBarStore} from "@/store/top-bar/useTopBar";
import {DesktopHeaderComponentProps} from "@/types/api/header";
import {usePathname} from "next/navigation";
import HeaderQueryHooks from "@/http/api/header/HeaderQueryHooks";
import {Skeleton} from "@/components/ui/skeleton";


const {useHeaderCategory} = new HeaderQueryHooks()

function DesktopHeaderComponent({
                                    topBarStatus,
                                    text,
                                    bgColor,
                                    textColor,
                                    hrefURl,
                                    backgroundImagePath
                                }: DesktopHeaderComponentProps) {

    const {changeTopBarStatus} = useTopBarStore()
    const {data, isPending} = useHeaderCategory()

    const categoriesProductCount = data?.data?.categoriesProductCount

    useEffect(() => {
        changeTopBarStatus(topBarStatus)
    }, [changeTopBarStatus, topBarStatus]);

    const [scrollToTop, setScrollToTop] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrollToTop(window.scrollY > 0);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const pathname = usePathname()

    if (pathname.includes("/auth")) return null

    return (
        <>
            <header
                className="w-full hidden left-0 duration-300 items-center border-b !bg-white px-2 !z-[600] md:flex justify-center">
                <ContainerLayout>
                    <section
                        className={`hidden md:flex w-full duration-300 items-end justify-between md:pt-[80px] px-3 md:flex-wrap ${
                            topBarStatus === "on" && "mt-[68px]"
                        }`}
                    >
                        <div
                            className={`transition-all duration-300 ${scrollToTop ? "fixed top-0 w-full right-0 z-20" : ""}`}>
                            {topBarStatus === "on" && (
                                <TopBarComponent
                                    text={text}
                                    bgColor={bgColor}
                                    textColor={textColor}
                                    hrefURl={hrefURl}
                                    backgroundImagePath={backgroundImagePath}
                                />
                            )}
                        </div>
                        <section
                            className={`${scrollToTop && "shadow-md duration-300"} bg-white fixed flex left-0 items-center justify-center w-full !z-[50]  ${topBarStatus === "on" ? "top-[68px]" : "top-0"}`}>
                            <section
                                className={`bg-white px-0 mx-auto container w-full py-2 transition-all duration-300  rounded-b-xl `}
                            >
                                <div
                                    className={`flex justify-between px-0 items-center w-full container mx-auto transition-all duration-300`}>
                                    <div className="w-1/4 flex items-center">
                                        <SearchHomeInput/>
                                    </div>
                                    <Link href="/" prefetch={false}
                                          className={"w-2/4 flex items-center justify-center"}>
                                        <figure className="w-full flex items-center justify-center">
                                            <Image priority src={ElanzaLogo || "/placeholder.svg"}
                                                   alt={"elanza-logo"} title={"elanza-logo"} width={198}
                                                   height={36}/>
                                        </figure>
                                    </Link>
                                    <div className="w-1/4 flex items-center justify-end">
                                        <nav className="w-[132px] flex gap-2 items-center justify-end h-[44px]">
                                            <BagButtonComponent
                                                hide={pathname.includes("/checkout") || pathname.includes("/address-select") || pathname.includes("/shopping-bag")}/>
                                            <UserButtonProvider>
                                                <UserButtonComponent/>
                                            </UserButtonProvider>
                                        </nav>
                                    </div>
                                </div>
                            </section>
                        </section>
                        <section dir={"rtl"} className="w-full px-0 items-center flex h-full flex-wrap list-none">
                            {!isPending ? <MegaMenu categoriesProductCount={categoriesProductCount}
                                                   data={data?.data?.categories}/> : <MegaMenuSkeleton/>}
                        </section>
                    </section>
                </ContainerLayout>
            </header>
        </>
    );
}

export default memo(DesktopHeaderComponent);
DesktopHeaderComponent.displayName = "DesktopHeaderComponent"


const MegaMenuSkeleton = () => {

    return (
        <div className={"w-full flex flex-wrap items-center justify-between"}>
            {Array.from({ length: 7 }).map((_, i) => (
                   <div key={i} className={"h-8 w-24"}>
                        <Skeleton className={"animate-pulse h-3 w-full"}/>
                   </div>
            ))}
        </div>
    )
}